import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ApprovalGate } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';

const GATE_COPY: Record<ApprovalGate, { title: string; detail: string }> = {
  SCOPE: {
    title: 'Scope awaiting approval',
    detail: 'Approving authorises testing against the hosts and window in this scope version.',
  },
  PLAN: {
    title: 'Test plan awaiting approval',
    detail: 'Approving permits active scanning, which sends payloads that can change state.',
  },
  REPORT: {
    title: 'Report awaiting approval',
    detail: 'Approving publishes the findings to the people who receive the report.',
  },
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function ApprovalsPage() {
  const pending = useQuery({ queryKey: ['approvals'], queryFn: api.listPendingApprovals });
  const items = pending.data?.items ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Governance</span>
          <h1>Approvals</h1>
        </div>
      </div>

      <p className="muted" style={{ maxWidth: '68ch', marginTop: -8 }}>
        Every gate is a decision a person makes. Nothing intrusive runs until one is recorded, and
        each decision is tied to the scope version it was made against.
      </p>

      <section className="card panel" aria-labelledby="queue-title">
        <h2 id="queue-title" className="panel-title">
          Waiting for a decision
        </h2>

        {pending.isLoading ? (
          <p className="muted">Loading the queue…</p>
        ) : pending.isError ? (
          <div role="alert">
            <p className="login-error">
              {pending.error instanceof ApiError
                ? pending.error.message
                : 'Could not load approvals.'}
            </p>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => void pending.refetch()}
            >
              Try again
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="empty">
            <p>Nothing is waiting.</p>
            <p className="muted">
              An engagement appears here when its scope, plan or report is submitted for approval.
            </p>
          </div>
        ) : (
          <div className="approval-queue">
            {items.map((item) => (
              <article key={`${item.engagementId}-${item.gate}`} className="card approval">
                <span className="gate-tag">Approval gate · {item.gate}</span>
                <h3>{GATE_COPY[item.gate].title}</h3>
                <p className="meta">{GATE_COPY[item.gate].detail}</p>
                <p className="meta">
                  <Link to={`/app/engagements/${item.engagementId}`} className="table-link">
                    {item.engagementName}
                  </Link>{' '}
                  · {item.environment} · scope v{item.scopeVersion}
                </p>
                <p className="meta">Submitted {formatDate(item.updatedAt)}</p>
                <div className="actions">
                  {/*
                    Decisions are made on the engagement, not here: an approver should read the
                    scope they are authorising before deciding, and the queue cannot show it.
                  */}
                  <Link
                    to={`/app/engagements/${item.engagementId}`}
                    className="btn btn-primary btn-sm"
                  >
                    Review and decide
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
